﻿import React, { useMemo, useContext, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Context, { context_type } from '@Util/Context';
import { Popup_Edit_Customer_Props, Customer } from '@Util/Types';
import { str_isEmpty } from '@Util/Utils';
import * as Api from '@Util/Api';
import { apiBase } from '@Util/GlobalValues';

import Popup from '@Elements/Popup/Popup';
import Input_With_Label from '@Elements/FormElements/Input_With_Label';
import Submit_Button from '@Elements/FormElements/Submit_Button';

export default function Edit_Customers_Popup(props: Popup_Edit_Customer_Props): JSX.Element {
    const { t } = useTranslation();
    const {
        customers,
        fetch_customers,
        set_status_update
    }: context_type = useContext(Context);

    const [name, set_name] = useState<string>("");
    const [name_error, set_name_error] = useState<string | null>(null);
    const [name_saving, set_name_saving] = useState<boolean>(false);

    const chosen_customer: Customer | null = useMemo(() => {
        return customers.find(customer => customer.id === props.chosen_customer_id) || null;
    }, [customers, props.chosen_customer_id]);

    useEffect(() => {
        if (chosen_customer) {
            set_name(chosen_customer.name);
        }
    }, [chosen_customer]);

    const handle_save_customer_name = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (name_saving) return;

        if (str_isEmpty(name)) {
            set_name_error(t('Error.CustomerNameEmpty'));
            return;
        }

        set_name_saving(true);
        set_name_error(null);

        try {
            const api_call = await Api.callApi(`${apiBase}/UpdateCustomer`, {
                originalName: chosen_customer?.name,
                name: name.trim()
            });

            if (api_call?.status === "Invalid" || api_call?.status === "Error") {
                set_status_update({
                    visible: true,
                    text: api_call?.details,
                    type: "Error"
                });
            } else {
                props.set_open(false);
                set_name("");
                set_status_update({
                    visible: true,
                    text: t('Text.ApiSaved'),
                    type: "Success"
                });
                fetch_customers();
            }
        } catch (err) {
            set_status_update({
                visible: true,
                text: err.message || t('Error.UnknownError'),
                type: "Error"
            });
        } finally {
            set_name_saving(false);
        }
    }, [chosen_customer, name, name_saving, fetch_customers, set_status_update, t, props]);

    return (
        <Popup
            open={props.open}
            set_open={props.set_open}
            title={`${t('Text.Edit')}: ${chosen_customer?.name}`}
            disable_close_action={false}
        >
            <form onSubmit={handle_save_customer_name}>
                <Input_With_Label
                    type="text"
                    id="edit_customer_name"
                    label={t('Label.Customer')}
                    value={name}
                    onChange={(e) => set_name(e.target.value)}
                    error={Boolean(name_error)}
                />
                <Submit_Button
                    text={t('Button.Save')}
                    disabled={name_saving}
                    loading={name_saving}
                />
                {name_error && <div className="error-message">{name_error}</div>}
            </form>
        </Popup>
    );
}